import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';
import { Map as ImmutableMap, List } from 'immutable';
// CORE
import React, { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

// MUI ICONS
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ExploreIcon from '@mui/icons-material/Explore';
import FlipIcon from '@mui/icons-material/Flip';
import MoneyOffRoundedIcon from '@mui/icons-material/MoneyOffRounded';
import NotInterestedRoundedIcon from '@mui/icons-material/NotInterestedRounded';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';

// MUI
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
import { accountDiscoveryTypes, aggErrors } from 'app-components/flux/account-discovery';
// BIT
import { accountsActions, accountsTypes, accountsUtils, accountsSelectors } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsSelectors, institutionLoginsTypes } from 'app-components/flux/institution-logins';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { tracker, isDemoMode, getLogger } from 'app-components/utils/core';
import * as fundingAccountActions from 'data/fundingAccounts/actions';

// DATA
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';

import { showInitiateTransferDialog, showMicrodespositDialog } from 'components/Dialogs/A2A/actions';
import { onboardA2ADialogIndexEnums } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';

import { showRealEstateDialog, showVehicleDialog, VehicleDialogOrigin } from 'components/Dialogs/AssetDialog/actions';
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';

// LOCAL

import { QButtonVariantEnum } from 'components/QButton';
import { QDropdownMenu, QMenuDivider, QMenuItem, QMenuSubheader } from 'components/QDropdowns';

import {
  DIALOG_TYPE as DIALOG_NOTIFICATION,
  mkNotificationDialogProps,
} from 'components/Dialogs/NotificationDialog/types';
import { DIALOG_TYPE_ACCOUNT_EDIT } from 'components/Dialogs/AccountEditDialog';
import { v4 as uuid } from 'uuid';

import styles from './styles';
import { vehicleSelectors } from 'app-components/flux/vehicle';
import { dispatchRequestDialog } from 'components/RequestDialog/dispatcher';

const useStyles = makeStyles()(styles);
const log = getLogger('AccountMenu');

const testAddFiDialogs = false;

type Props = {
  accountId?: string;
  compact?: boolean;
  errorBannerShowing?: boolean;
  hideClose?: boolean;
  hideMakeManual?: boolean;
  hideDelete?: boolean;
  onViewAccount?: () => void;
};

const AccountMenu = (props: Props) => {
  const { accountId, compact, errorBannerShowing, hideClose, hideMakeManual, hideDelete, onViewAccount } = props;

  const account = useAppSelector((state) =>
    accountId ? accountsSelectors.getAccountById(state, accountId) : undefined,
  );

  const institutionLogin = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionLoginForId(state, account?.institutionLoginId || ''),
  );

  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  // ====================================== Component State ================================================ //
  const [showDebugOptions, setShowDebugOptions] = useState(false);
  const showZillowConnect = account && !account.isConnected && !account.isClosed && account.type === 'REAL_ESTATE';
  const isStripeAccount = accountsUtils.isStripeAccount(account);
  const isARAccount = account?.type === 'ACCOUNTS_RECEIVABLE';

  // ============================ TESTING KEY WATCHER =========================== //
  const handleDebugKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Alt') setShowDebugOptions(true);
  }, []);
  const handleDebugKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Alt') setShowDebugOptions(false);
  }, []);
  useEffect(() => {
    document.addEventListener('keydown', handleDebugKeyDown);
    document.addEventListener('keyup', handleDebugKeyUp);
    return () => {
      document.removeEventListener('keydown', handleDebugKeyDown);
      document.removeEventListener('keyup', handleDebugKeyUp);
    };
  }, [handleDebugKeyDown, handleDebugKeyUp]);

  // ====================================== Redux Selectors ================================================ //
  const featureFlags = useAppSelector(featureFlagsSelectors.getFeatureFlags);
  const a2aTransferEnabled = useAppSelector(a2aTransfersSelectors.a2aTransferEnabled);

  const isTransferSetupEligible = useAppSelector((state) =>
    account ? fundingAccountSelectors.isAccountEligibleForTransfers(state, account) : undefined,
  );
  const isMicrodepositEligible = useAppSelector((state) =>
    account ? fundingAccountSelectors.isAccountEligibleForMicrodeposits(state, account) : undefined,
  );
  const isMoveMoneyEligible = useAppSelector((state) =>
    account ? fundingAccountSelectors.isAccountEligibleForCashTransfer(state, account) : undefined,
  );
  const isDisabled = useAppSelector((state) =>
    account ? fundingAccountSelectors.isAccountDisabled(state, account) : undefined,
  );
  const fundingAccount = useAppSelector((state) =>
    fundingAccountSelectors.getFundingAccountById(state, account?.fundingAccountId),
  );

  const allInstitutionsRefreshing = useAppSelector(institutionLoginsSelectors.getIsRefreshingAll);
  const thisInstitutionRefreshing = useAppSelector(institutionLoginsSelectors.getIdsRefreshing);

  const showKbbFlowOnEditAccount = useAppSelector((state) =>
    vehicleSelectors.selectShowKbbFlowOnEditAccount(state, account?.id),
  );

  // ===================================== Redux Util Values ============================================== //
  const isError = accountsUtils.isAccountInError(account)?.isError;
  const isDisconnected = accountsUtils.isAccountDisconnected(account, institutionLogin);

  // ====================================== Callbacks ================================================ //
  const isErrorBeingFixed = useCallback((): boolean => {
    const aggStatus = account?.aggregators?.get(0)?.aggStatus;
    return (
      Boolean(institutionLogin) &&
      Boolean(institutionLogin?.id && thisInstitutionRefreshing.has(institutionLogin.id)) &&
      !allInstitutionsRefreshing &&
      Boolean((aggStatus != null && aggStatus !== 'OK') || institutionLogin?.optionalAction)
    );
  }, [account, allInstitutionsRefreshing, institutionLogin, thisInstitutionRefreshing]);

  const dispatchQCSAccountAction = useCallback(
    (id: string, qcsAction: string) => {
      if (id && qcsAction) dispatch(accountsActions.performAccountAction({ id, qcsAction }));
    },
    [dispatch],
  );

  const handleUpdateAccount = useCallback(
    (
      acc: accountsTypes.Account,
      accountAction: string | null,
      params: Record<string, unknown> = {},
      undoMessage?: string,
    ) => {
      const options = { id: acc.id, ...params };
      const type = acc?.type;

      actionHelpers
        .bindPromiseAction(dispatch, accountsActions.updateAccount)(accountsTypes.mkUpdateAccount(type, options), {
          ...(undoMessage ? { undo: { userMessage: undoMessage, disableUndoAction: true } } : {}),
        })
        .then(() => {
          if (accountAction && acc?.id) {
            dispatchQCSAccountAction(acc.id, accountAction);
          }
        })
        .catch((error) => {
          dispatch(
            rootUiActions.createDialog(
              rootUiTypes.mkRootUiData({
                id: 'error-update-account-dialog',
                type: DIALOG_NOTIFICATION,
                props: ImmutableMap(
                  mkNotificationDialogProps({
                    title: 'Account update failure',
                    content: (
                      <Typography variant='titleRegular' className={classes.errorWrapper}>
                        {error.response?.data?.errors[0]?.detail ||
                          error.response?.data?.errors[0]?.title ||
                          'Sorry, there seems to be a problem on our end. Please try again later.'}
                        {error?.response?.status && (
                          <Typography
                            className={classes.errorCodeText}
                          >{`Error Code: ${error?.response?.status}`}</Typography>
                        )}
                      </Typography>
                    ),
                  }),
                ),
              }),
            ),
          );
        });
    },
    [classes.errorCodeText, classes.errorWrapper, dispatch, dispatchQCSAccountAction],
  );

  const deleteFundingAccount = useCallback(() => {
    const deleteMessage = (
      <Box display='flex' flexDirection='column'>
        <Typography variant='titleRegular'>
          This will delete the funding account connected to {account?.name}? This is for devs only.
        </Typography>
        <Typography variant='titleRegular' className={classes.textSpacing}>
          Note: The fundingAccount will NOT be deleted in dwolla unless you go to dwolla and delete it. Contact Austin
          or Jabbar on how to do this.
        </Typography>
      </Box>
    );

    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-funding-account',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              size: 'sm',
              title: 'Delete funding account',
              content: deleteMessage,
              confirmButtonVariant: QButtonVariantEnum.DELETE,
              denyButtonVariant: QButtonVariantEnum.OUTLINED,
              confirmLabel: 'Delete',
              denyLabel: 'Cancel',
              confirmAction: () => {
                if (fundingAccount?.id) {
                  dispatch(fundingAccountActions.deleteFundingAccount({ id: fundingAccount.id, isDeleted: true }));
                }
              },
            }),
          ),
        }),
      ),
    );
  }, [account?.name, classes.textSpacing, dispatch, fundingAccount]);

  const handleDisableTransfers = useCallback(() => {
    const disableMessage = (
      <Box display='flex' flexDirection='column'>
        <Typography variant='titleRegular'>
          Disable transfers for {account?.name}? Any transfers involving this account that are already processing will
          still be completed.
        </Typography>
        <Typography variant='titleRegular' className={classes.textSpacing}>
          Note: this won&apos;t affect existing account data or the downloading of new transactions. You&apos;ll need to
          re-verify this account to use it for transfers in the future.
        </Typography>
      </Box>
    );

    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'disable-transfers',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              size: 'sm',
              title: 'Disable account transfers',
              content: disableMessage,
              confirmButtonVariant: QButtonVariantEnum.DELETE,
              denyButtonVariant: QButtonVariantEnum.OUTLINED,
              confirmLabel: 'disable',
              denyLabel: 'Cancel',
              confirmAction: () =>
                handleUpdateAccount(
                  account as accountsTypes.Account,
                  null,
                  { fundingAccountId: '' },
                  'Transfers disabled',
                ),
            }),
          ),
        }),
      ),
    );
  }, [account, dispatch, handleUpdateAccount, classes.textSpacing]);

  const handleExcludeFromReports = useCallback(
    (checked: boolean) => {
      handleUpdateAccount(account as accountsTypes.Account, null, { isExcludedFromReports: checked });
    },
    [account, handleUpdateAccount],
  );

  const handleExcludeFromSpendingPlan = useCallback(
    (checked: boolean) => {
      handleUpdateAccount(account as accountsTypes.Account, null, { isExcludedFromBudgets: checked });
    },
    [account, handleUpdateAccount],
  );

  const deleteAccount = useCallback(() => {
    //  dispatch(accountsActions.deleteAccount(account));
    actionHelpers
      .bindPromiseAction(
        dispatch,
        accountsActions.deleteAccount,
      )(account)
      .then(() => {
        //  TODO: Implement official 'confirmation' dialog Ashim designed
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'account-deletion-confirmation-dialog',
              type: DIALOG_NOTIFICATION,
              props: ImmutableMap(
                mkNotificationDialogProps({
                  title: 'Delete account Success',
                  content: <Typography variant='titleRegular'> Your account has been successfully deleted.</Typography>,
                }),
              ),
            }),
          ),
        );
      })
      .catch((error) => {
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'account-deletion-failure-dialog',
              type: DIALOG_NOTIFICATION,
              props: ImmutableMap(
                mkNotificationDialogProps({
                  title: 'Account deletion failure',
                  content: (
                    <Typography variant='titleRegular' className={classes.errorWrapper}>
                      {error.response?.data?.errors[0]?.detail ||
                        error.response?.data?.errors[0]?.title ||
                        'Sorry, there seems to be a problem on our end. Please try again later.'}
                      {error?.response?.status && (
                        <Typography
                          className={classes.errorCodeText}
                        >{`Error Code: ${error?.response?.status}`}</Typography>
                      )}
                    </Typography>
                  ),
                }),
              ),
            }),
          ),
        );
      });
  }, [account, classes.errorCodeText, classes.errorWrapper, dispatch]);

  const makeAccountManual = useCallback(() => {
    if (account?.id) {
      dispatchQCSAccountAction(account.id, 'make-manual');
    } else {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'make-account-manual-failure-dialog',
            type: DIALOG_NOTIFICATION,
            props: ImmutableMap(
              mkNotificationDialogProps({
                title: 'Account change failure',
                content: (
                  <Typography variant='titleRegular' className={classes.errorWrapper}>
                    The account could not be found
                  </Typography>
                ),
              }),
            ),
          }),
        ),
      );
    }
  }, [account, classes.errorWrapper, dispatch, dispatchQCSAccountAction]);

  const closeAccount = useCallback(() => {
    if (account?.id) {
      dispatchQCSAccountAction(account.id, 'close');
    } else {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'close-account-failure-dialog',
            type: DIALOG_NOTIFICATION,
            props: ImmutableMap(
              mkNotificationDialogProps({
                title: 'Account change failure',
                content: (
                  <Typography variant='titleRegular' className={classes.errorWrapper}>
                    The account could not be found
                  </Typography>
                ),
              }),
            ),
          }),
        ),
      );
    }
  }, [account, classes.errorWrapper, dispatch, dispatchQCSAccountAction]);

  /* ======================================= A2A Kickoff Hook ========================================= */
  //  The logic used to determine what dialog to open, email, identity, or transfer
  const kickoffA2AFlow = useKickoffA2AFlow();

  // ====================================== Dialog Kickoffs ================================================ //
  const kickoffCloseAccount = useCallback(() => {
    const closeMessage =
      account?.type !== 'INVESTMENT' ? (
        <span>
          Closing {account?.name} will result in the following actions: <br />
          <ul>
            <li>The account balance will be set to $0.</li>
            <li>Downloading of future transactions in Simplifi will be disabled.</li>
            <li>Note that you will still have access to view all of your past transactions.</li>
          </ul>
          Are you sure you want to proceed with closing the account?
        </span>
      ) : (
        <span>
          Closing {account?.name} will result in the following actions: <br />
          <ul>
            <li>The account balance will be set to $0.</li>
            <li>All holdings associated with the account will be removed.</li>
            <li>Downloading of future transactions in Simplifi will be disabled.</li>
            <li>Note that you will still have access to view all of your past transactions.</li>
          </ul>
          Are you sure you want to proceed with closing the account?
        </span>
      );
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'close-account',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Close account',
              content: closeMessage,
              confirmLabel: 'Confirm',
              denyLabel: 'Cancel',
              confirmAction: () => closeAccount(),
            }),
          ),
        }),
      ),
    );
  }, [dispatch, account, closeAccount]);

  const kickoffMakeManual = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'make-account-manual',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Make account manual',
              content: `Are you sure you want to make ${account?.name} manual? This will ${
                accountsUtils.isAccountConnected(account) ? 'disconnect from your bank and' : ''
              } move the account to the manual section.`,
              confirmLabel: 'Make Manual',
              denyLabel: 'Cancel',
              confirmAction: () => makeAccountManual(),
            }),
          ),
        }),
      ),
    );
  }, [dispatch, account, makeAccountManual]);

  const kickoffDeleteAccount = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-account',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Delete account',
              content: `Deleting ${account?.name} will remove all of its data and transactions from Simplifi. This will not fix connection issues.`,
              confirmLabel: 'Delete',
              denyLabel: 'Cancel',
              confirmButtonVariant: QButtonVariantEnum.DELETE,
              confirmAction: () => deleteAccount(),
            }),
          ),
        }),
      ),
    );
  }, [dispatch, account, deleteAccount]);

  const kickoffEditAccount = useCallback(() => {
    if (showKbbFlowOnEditAccount && typeof account?.id === 'string') {
      dispatch(showVehicleDialog({ linkTo: account.id, dialogOrigin: VehicleDialogOrigin.EditAccount }));
    } else if (account?.id) {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: `edit-account-${uuid()}`,
            type: DIALOG_TYPE_ACCOUNT_EDIT,
            allowNesting: true,
            props: ImmutableMap({
              accountId: account.id,
            }),
          }),
        ),
      );
    }
  }, [account, dispatch, showKbbFlowOnEditAccount]);

  const kickoffMicrodepositFlow = useCallback(
    (initiator: string) => {
      dispatch(
        showMicrodespositDialog({
          fundingAccountId: fundingAccount?.id,
          hideAccountPicker: true,
          initiator,
        }),
      );
    },
    [fundingAccount, dispatch],
  );

  const kickoffZillowConnect = useCallback(() => {
    tracker.track(tracker.events.addAssetsUX_1, { userAction: 'real estate' });
    dispatch(showRealEstateDialog(account?.id ?? undefined));
  }, [account?.id, dispatch]);

  const kickoffTransferFlow = useCallback(() => {
    dispatch(
      showInitiateTransferDialog({
        initiator: 'account level details',
        transferOptions: {
          fromAccount: account,
        },
      }),
    );
  }, [account, dispatch]);

  const launchUpdateCredentialsDialog = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: uuid(),
          type: accountDiscoveryTypes.DIALOG_TYPE_ACCOUNTS_DISCOVERY,
          props: ImmutableMap(
            accountDiscoveryTypes.mkAccountDiscoveryDialogProps({
              mode: 'UPDATE-CREDENTIALS',
              institutionLogin,
            }),
          ),
        }),
      ),
    );
  }, [dispatch, institutionLogin]);

  const launchMfaChallengeDialog = useCallback(() => {
    const challenges = List.of(institutionLoginsTypes.mkMfaChallengeItem({ question: 'Test Question' }));
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: uuid(),
          type: accountDiscoveryTypes.DIALOG_TYPE_ACCOUNTS_DISCOVERY,
          props: ImmutableMap(
            accountDiscoveryTypes.mkAccountDiscoveryDialogProps({
              mode: 'PROVIDE-MFA',
              institutionLogin,
              mfaChallenge: institutionLoginsTypes.mkMfaChallenge({
                institutionLoginId: institutionLogin?.id,
                challenges,
              }),
            }),
          ),
        }),
      ),
    );
  }, [dispatch, institutionLogin]);

  // ======================================= Account Status ====================================== //
  const accountStatus = {
    eligible: {
      showChip: a2aTransferEnabled && isTransferSetupEligible?.status, // "showChip" is used to track the status of the account
      enableTransferFunctions: a2aTransferEnabled && isTransferSetupEligible?.isEligible,
    },
    ineligible: {
      showChip: false,
      enableTransferFunctions: a2aTransferEnabled && !isTransferSetupEligible?.isEligible,
    },
    depositPending: {
      showChip: a2aTransferEnabled && isMicrodepositEligible?.status,
      enableTransferFunctions: a2aTransferEnabled && isMicrodepositEligible?.isEligible,
      manualVerification: a2aTransferEnabled && isMicrodepositEligible?.isManualVerified,
    },
    verified: {
      showChip: a2aTransferEnabled && isMoveMoneyEligible?.status,
      enableTransferFunctions: a2aTransferEnabled && isMoveMoneyEligible?.isEligible,
    },
    fundingAccountFailure: {
      showChip: a2aTransferEnabled && isDisabled?.status,
    },
    error: {
      showChip: isError,
    },
    closed: {
      showChip: account?.isClosed,
    },
    disconnected: {
      showChip: isDisconnected && !account?.isClosed && !isDemoMode(),
    },
  };

  let ineligibleText = '';
  if (accountStatus.depositPending.showChip && !accountStatus.depositPending.enableTransferFunctions) {
    ineligibleText = isMicrodepositEligible?.reasonString ?? '';
  } else if (accountStatus.verified.showChip && !accountStatus.verified.enableTransferFunctions) {
    ineligibleText = isMoveMoneyEligible?.reasonString ?? '';
  } else if (accountStatus.fundingAccountFailure.showChip) {
    ineligibleText = isDisabled?.reasonString ?? '';
  } else {
    ineligibleText = isTransferSetupEligible?.reasonString ?? '';
  }

  // ============================================================================================ //

  const showExcludeFromSpendingPlan = Boolean(featureFlags.get('reportsIgnoreExcludedAccounts'));
  const showExcludeFromReports = Boolean(featureFlags.get('ignoreAccount'));
  const showExcludeSection = showExcludeFromSpendingPlan || showExcludeFromReports;
  const showMakeManual = !hideMakeManual && Boolean(institutionLogin || (account?.isConnected && account?.aggregators));
  const showMarkClosed =
    !hideClose &&
    !account?.isClosed &&
    institutionLogin?.channel !== 'APPLE_WALLET' &&
    Boolean(featureFlags.get('closeAccount')) &&
    !isARAccount;
  const showDelete = !hideDelete && !isARAccount;
  const showDisableTransfers =
    a2aTransferEnabled &&
    Boolean(
      accountStatus.verified.showChip ||
        accountStatus.depositPending.showChip ||
        accountStatus.fundingAccountFailure.showChip,
    );

  const handleFixError = useCallback(() => {
    if (isErrorBeingFixed()) return;
    const aggregation = account?.aggregators?.get(0);
    const aggAction = aggErrors.getQcsAggregationAction(
      institutionLogin,
      aggregation?.aggStatus,
      aggregation?.aggStatusCode,
      featureFlags,
    );
    if (aggAction) {
      aggErrors.handleAggAction(dispatch, aggAction, 'settings account row');
    } else {
      log.error(
        'No aggAction available for Fix Error menu option',
        '\nAccount ID:',
        account?.id,
        '\nAccount:',
        account?.name,
        '\nInstitution Login ID:',
        institutionLogin?.id,
        '\nInstitution:',
        institutionLogin?.name,
        '\nAgg Status:',
        aggregation?.aggStatus,
        '\nAgg Status Code:',
        aggregation?.aggStatusCode,
      );
    }
  }, [account, dispatch, featureFlags, institutionLogin, isErrorBeingFixed]);

  if (compact) {
    const showFixError = institutionLogin?.channel !== 'APPLE_WALLET' && accountStatus.error.showChip;

    return (
      <QDropdownMenu
        id={`wallet-account-menu-${accountId ?? 'unknown'}`}
        aria-label={`More options for ${account?.name ?? 'credit card'}`}
        width={267}
        iconButtonSize='small'
        iconButtonVariant='plain'
      >
        {showFixError ? (
          <QMenuItem
            variant='action'
            label='Fix error'
            id='fix-error-btn'
            startIcon={<ErrorRoundedIcon />}
            disabled={isErrorBeingFixed()}
            onClick={handleFixError}
          />
        ) : null}
        {showFixError ? <QMenuDivider /> : null}
        <QMenuItem
          variant='action'
          label='View account'
          id='view-account'
          startIcon={<OpenInBrowserIcon />}
          onClick={() => onViewAccount?.()}
        />
        <QMenuDivider />
        <QMenuItem
          variant='action'
          label='Edit account'
          id='edit-account'
          startIcon={<EditIcon />}
          onClick={kickoffEditAccount}
        />
        {showMakeManual ? <QMenuDivider /> : null}
        {showMakeManual ? (
          <QMenuItem
            variant='action'
            label='Make manual'
            id='account-make-manual'
            startIcon={<FlipIcon />}
            onClick={kickoffMakeManual}
          />
        ) : null}
        {showMarkClosed ? <QMenuDivider /> : null}
        {showMarkClosed ? (
          <QMenuItem
            variant='action'
            label='Mark as closed'
            id='account-close'
            startIcon={<NotInterestedRoundedIcon />}
            onClick={kickoffCloseAccount}
          />
        ) : null}
        {showDelete ? <QMenuDivider /> : null}
        {showDelete ? (
          <QMenuItem
            variant='action'
            label='Delete account'
            id='account-delete'
            destructive
            startIcon={<DeleteIcon />}
            onClick={kickoffDeleteAccount}
          />
        ) : null}
      </QDropdownMenu>
    );
  }

  if (isStripeAccount) {
    return (
      <IconButton
        size='small'
        shape='rounded'
        variant='tonal'
        className={classes.threeDotButton}
        id='account-menu-button'
        aria-label='Edit account'
        onClick={kickoffEditAccount}
      >
        <EditIcon />
      </IconButton>
    );
  }

  return (
    <QDropdownMenu
      id='account-menu'
      aria-label='More menu'
      width={280}
      iconButtonSize='small'
      IconButtonProps={{ className: classes.threeDotButton }}
    >
      {institutionLogin?.channel !== 'APPLE_WALLET' && accountStatus.error.showChip && !errorBannerShowing ? (
        <QMenuItem
          variant='action'
          label='Fix error'
          id='fix-error-btn'
          startIcon={<ErrorRoundedIcon />}
          disabled={isErrorBeingFixed()}
          onClick={handleFixError}
        />
      ) : null}
      {institutionLogin?.channel !== 'APPLE_WALLET' && accountStatus.error.showChip && !errorBannerShowing ? (
        <QMenuDivider />
      ) : null}

      <QMenuItem
        variant='action'
        label='Edit account'
        id='edit-account'
        startIcon={<EditIcon />}
        onClick={kickoffEditAccount}
      />
      <QMenuDivider />

      {a2aTransferEnabled ? (
        accountStatus.eligible.enableTransferFunctions ? (
          <QMenuItem
            variant='action'
            label='Set up account transfers'
            id='setup-transfer-account'
            startIcon={<SwapHorizRoundedIcon />}
            onClick={() => {
              kickoffA2AFlow({
                initiator: 'account level details',
                startTabIndex: onboardA2ADialogIndexEnums.ACCT_INFO,
                initialInstitutionId: institutionLogin?.institutionId ?? undefined,
                initialAccountId: account?.id ?? undefined,
              });
            }}
          />
        ) : accountStatus.depositPending.enableTransferFunctions ? (
          <QMenuItem
            variant='action'
            label='Verify account for transfers'
            id='verify-deposits-account'
            startIcon={<SwapHorizRoundedIcon />}
            onClick={() => kickoffMicrodepositFlow('account menu')}
          />
        ) : accountStatus.verified.enableTransferFunctions ? (
          <QMenuItem
            variant='action'
            label='Transfer money'
            id='transfer-money'
            startIcon={<SwapHorizRoundedIcon />}
            onClick={kickoffTransferFlow}
          />
        ) : (
          <QMenuItem
            variant='action'
            label='Not eligible for transfers'
            id='setup-transfer-account'
            subtext={ineligibleText}
            startIcon={<SwapHorizRoundedIcon />}
            disabled
            onClick={() => {}}
          />
        )
      ) : null}
      {a2aTransferEnabled ? <QMenuDivider /> : null}
      {a2aTransferEnabled && showDisableTransfers ? (
        <QMenuItem
          variant='action'
          label='Disable account transfers'
          id='disable-transfer-money'
          startIcon={<MoneyOffRoundedIcon />}
          onClick={handleDisableTransfers}
        />
      ) : null}
      {a2aTransferEnabled && showDisableTransfers ? <QMenuDivider /> : null}

      {showExcludeSection ? <QMenuSubheader label='Exclude account transactions from:' /> : null}
      {showExcludeFromSpendingPlan ? (
        <QMenuItem
          variant='toggle'
          label='Spending Plan'
          id={account?.isExcludedFromBudgets ? 'include-in-spending-plan' : 'ignore-from-spending-plan'}
          checked={Boolean(account?.isExcludedFromBudgets)}
          onCheckedChange={handleExcludeFromSpendingPlan}
        />
      ) : null}
      {showExcludeFromReports ? (
        <QMenuItem
          variant='toggle'
          label='Reports'
          id={account?.isExcludedFromReports ? 'include-in-reports' : 'ignore-from-reports'}
          checked={Boolean(account?.isExcludedFromReports)}
          onCheckedChange={handleExcludeFromReports}
        />
      ) : null}
      {showExcludeSection && (showZillowConnect || showMakeManual || showMarkClosed || showDelete) ? (
        <QMenuDivider />
      ) : null}

      {showZillowConnect ? (
        <QMenuItem
          variant='action'
          label='Connect'
          id='connect-zillow'
          startIcon={<ExploreIcon />}
          onClick={kickoffZillowConnect}
        />
      ) : null}
      {showZillowConnect ? <QMenuDivider /> : null}

      {showMakeManual ? (
        <QMenuItem
          variant='action'
          label='Make manual'
          id='account-make-manual'
          startIcon={<FlipIcon />}
          onClick={kickoffMakeManual}
        />
      ) : null}
      {showMakeManual && (showMarkClosed || showDelete) ? <QMenuDivider /> : null}

      {showMarkClosed ? (
        <QMenuItem
          variant='action'
          label='Mark as closed'
          id='account-close'
          startIcon={<NotInterestedRoundedIcon />}
          onClick={kickoffCloseAccount}
        />
      ) : null}
      {showMarkClosed && showDelete ? <QMenuDivider /> : null}

      {showDelete ? (
        <QMenuItem
          variant='action'
          label='Delete account'
          id='account-delete'
          destructive
          startIcon={<DeleteIcon />}
          onClick={kickoffDeleteAccount}
        />
      ) : null}

      {process.env.NODE_ENV !== 'production' && showDebugOptions && fundingAccount ? (
        <QMenuItem
          variant='action'
          label='Delete funding account'
          id='delete-funding-account'
          destructive
          startIcon={<DeleteIcon />}
          onClick={() => {
            handleUpdateAccount(account as accountsTypes.Account, null, { fundingAccountId: '' });
            deleteFundingAccount();
          }}
        />
      ) : null}

      {testAddFiDialogs && institutionLogin ? <QMenuDivider /> : null}
      {testAddFiDialogs && institutionLogin ? (
        <QMenuItem
          variant='action'
          label='Test Update Credentials'
          id='account-test-update-credentials'
          startIcon={<FlipIcon />}
          onClick={launchUpdateCredentialsDialog}
        />
      ) : null}
      {testAddFiDialogs && institutionLogin ? (
        <QMenuItem
          variant='action'
          label='Test MFA Challenge'
          id='account-test-mfa-challenge'
          startIcon={<FlipIcon />}
          onClick={launchMfaChallengeDialog}
        />
      ) : null}

      {featureFlags.get('dev') ? <QMenuDivider /> : null}
      {featureFlags.get('dev') ? (
        <QMenuItem
          variant='action'
          label='Account API call'
          id='account-api'
          startIcon={<BuildOutlinedIcon />}
          closeOnClick
          endAction={<Chip label='Dev only' size='small' variant='tonal' color='warning' />}
          onClick={() => {
            dispatchRequestDialog({
              method: 'PUT',
              path: `/accounts/${account?.id}`,
              body: JSON.stringify(account, null, 2),
            });
          }}
        />
      ) : null}
    </QDropdownMenu>
  );
};

export default AccountMenu;
